/**
 * ClassSilhouette.jsx — bundled SVG hero silhouettes, one per class.
 *
 * Rendered as the ALWAYS-VISIBLE base layer of the portrait panel so the
 * player never sees a "PORTRAIT LOST" state even when the AI-generated
 * portrait fails to load. Zero network — ships in the JS bundle.
 *
 * Each silhouette is drawn to the panel's aspect, tinted with the class's
 * ember / accent color, and sits behind the real portrait which fades in
 * on top when it finishes loading.
 */

const CLASS_THEMES = {
  warrior: { accent: "#e25822", glow: "rgba(226, 88, 34, 0.35)", label: "WARRIOR" },
  wizard:  { accent: "#7aa8ff", glow: "rgba(122, 168, 255, 0.35)", label: "WIZARD"  },
  rogue:   { accent: "#9d6cf0", glow: "rgba(157, 108, 240, 0.35)", label: "ROGUE"   },
  cleric:  { accent: "#f0d87a", glow: "rgba(240, 216, 122, 0.35)", label: "CLERIC"  },
  paladin: { accent: "#f0e6d2", glow: "rgba(240, 230, 210, 0.35)", label: "PALADIN" },
  ranger:  { accent: "#5ac77a", glow: "rgba(90, 199, 122, 0.35)",  label: "RANGER"  },
};

const DEFAULT_THEME = CLASS_THEMES.warrior;

/** Hooded armored bust — works for all classes, differentiated by color + overlay. */
function HeroBust({ accent, glow }) {
  return (
    <svg
      viewBox="0 0 200 260"
      preserveAspectRatio="xMidYMid slice"
      className="w-full h-full"
      aria-hidden="true"
    >
      <defs>
        <radialGradient id="bg-emb" cx="50%" cy="40%" r="65%">
          <stop offset="0%"  stopColor={glow} />
          <stop offset="55%" stopColor="rgba(16,8,4,0.9)" />
          <stop offset="100%" stopColor="rgba(8,4,2,1)" />
        </radialGradient>
        <linearGradient id="armor-grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"  stopColor="#2a1810" />
          <stop offset="50%" stopColor="#4a2d1a" />
          <stop offset="100%" stopColor="#1a0e08" />
        </linearGradient>
        <linearGradient id="hood-grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"  stopColor="#1f1109" />
          <stop offset="100%" stopColor="#0a0503" />
        </linearGradient>
      </defs>

      {/* Backdrop ember glow */}
      <rect width="200" height="260" fill="url(#bg-emb)" />

      {/* Hood + shoulders silhouette */}
      <path
        d="M 20 260 L 20 180 Q 30 130 60 110 Q 50 75 70 50 Q 85 25 100 25 Q 115 25 130 50 Q 150 75 140 110 Q 170 130 180 180 L 180 260 Z"
        fill="url(#hood-grad)"
      />

      {/* Face shadow — intentionally dark, enigmatic */}
      <ellipse cx="100" cy="105" rx="30" ry="36" fill="#0a0503" />

      {/* Pauldron / armor plates */}
      <path
        d="M 25 185 Q 55 165 75 180 L 75 215 Q 50 220 25 215 Z"
        fill="url(#armor-grad)"
        stroke={accent}
        strokeWidth="1.2"
        opacity="0.9"
      />
      <path
        d="M 175 185 Q 145 165 125 180 L 125 215 Q 150 220 175 215 Z"
        fill="url(#armor-grad)"
        stroke={accent}
        strokeWidth="1.2"
        opacity="0.9"
      />

      {/* Chest plate crest */}
      <path
        d="M 75 180 Q 100 170 125 180 L 128 230 Q 100 240 72 230 Z"
        fill="url(#armor-grad)"
        stroke={accent}
        strokeWidth="1"
      />

      {/* Class sigil — diamond with inner glow */}
      <g transform="translate(100 205)">
        <path d="M 0 -12 L 10 0 L 0 12 L -10 0 Z" fill={accent} opacity="0.75" />
        <path d="M 0 -6 L 5 0 L 0 6 L -5 0 Z" fill="#fff" opacity="0.9" />
      </g>

      {/* Two ember eyes glowing in the hood */}
      <circle cx="90"  cy="105" r="2.5" fill={accent} opacity="0.95">
        <animate attributeName="opacity" values="0.6;1;0.6" dur="2.4s" repeatCount="indefinite" />
      </circle>
      <circle cx="110" cy="105" r="2.5" fill={accent} opacity="0.95">
        <animate attributeName="opacity" values="0.6;1;0.6" dur="2.4s" repeatCount="indefinite" begin="0.3s" />
      </circle>

      {/* Hood rim highlight */}
      <path
        d="M 55 95 Q 75 55 100 50 Q 125 55 145 95"
        fill="none"
        stroke={accent}
        strokeWidth="0.8"
        opacity="0.45"
      />
    </svg>
  );
}

/**
 * ClassSilhouette — the always-visible base layer.
 * @param {string} classKey — "warrior" | "wizard" | "rogue" | "cleric" | "paladin" | "ranger"
 */
export default function ClassSilhouette({ classKey }) {
  const theme = CLASS_THEMES[(classKey || "").toLowerCase()] || DEFAULT_THEME;
  return (
    <div
      className="absolute inset-0 overflow-hidden"
      data-testid={`class-silhouette-${classKey || "unknown"}`}
      style={{ background: "#0a0503" }}
    >
      <HeroBust accent={theme.accent} glow={theme.glow} />
    </div>
  );
}

export { CLASS_THEMES };
